import {
  PermissionResourceType,
  useCheckResourcePermissionsQuery,
} from 'src/generated/graphql';
import { NamespacePermission } from 'src/common/permissions';

/**
 * Permissions this hook asks about. Joined into a comma-separated string at the query boundary,
 * because the GraphQL argument must be a scalar to appear in the resolver's caching key.
 */
const REQUESTED_PERMISSIONS = [NamespacePermission.SourceManagement];

/**
 * Whether the caller may perform source mutations in `namespaceId`.
 *
 * FAILS CLOSED - false while in flight, on error, and when there is no namespace to ask about.
 * The alternative puts an enabled button in front of a user who cannot use it, and whoever
 * clicks in that window gets a 403 from an action the UI offered them.
 *
 * A `true` only enables a control. Every mutation is authorized server-side at request time.
 *
 * Uses `no-cache`: an authorization answer should have one cache with one explicit TTL, and that
 * is the resolver's `cachingConfig`. A second copy in the browser would keep a revoked grant
 * alive for the life of the tab.
 *
 * Costs one request per navigation, not per component - Apollo deduplicates identical in-flight
 * operations regardless of fetch policy.
 */
export interface NamespacePermissions {
  /** Whether the caller may perform source mutations. False until confirmed. */
  canManageSource: boolean;
  /**
   * Whether the answer is still in flight. Distinct from `canManageSource` being false, which is
   * a settled denial.
   */
  loading: boolean;
}

export const useNamespacePermissions = (
  namespaceId: string | undefined,
): NamespacePermissions => {
  const { data, error, loading } = useCheckResourcePermissionsQuery({
    variables: {
      resourceType: PermissionResourceType.Namespace,
      resourceId: namespaceId ?? '',
      permissions: REQUESTED_PERMISSIONS.join(','),
    },
    fetchPolicy: 'no-cache',
    skip: !namespaceId,
  });

  // No namespace to ask about is a settled false, not a pending one: a skipped query never
  // resolves, so reporting it as loading would hold controls in a skeleton forever.
  if (!namespaceId || error) {
    return { canManageSource: false, loading: false };
  }

  // Matched by name rather than by position: the resolver returns the requested
  // names in order, but a caller that later asks for more permissions should not
  // silently start reading a different one.
  return {
    canManageSource:
      data?.checkResourcePermissions.permissions.some(
        (entry) =>
          entry.permission === NamespacePermission.SourceManagement &&
          entry.granted,
      ) ?? false,
    loading,
  };
};
